/**
 * RevisionExplainerCard — "Why did this change?" panel.
 *
 * Renders the server's classification (label + confidence + key
 * signals) for the currently-selected revision. The classifier lives
 * in services/quotation_behavior_intelligence — UI is pure
 * presentation, never duplicates the logic.
 */
import React, { useEffect, useState } from 'react';
import { axiosInstance } from '../App';
import { Brain, ChevronDown, ChevronUp } from 'lucide-react';

const REASON_META = {
  price_objection:        { label: 'Price Objection',        tone: 'bg-red-50 text-red-800 border-red-200' },
  quantity_negotiation:   { label: 'Quantity Negotiation',   tone: 'bg-amber-50 text-amber-800 border-amber-200' },
  scope_change:           { label: 'Scope Change',           tone: 'bg-blue-50 text-blue-800 border-blue-200' },
  contract_clarification: { label: 'Contract Clarification', tone: 'bg-indigo-50 text-indigo-800 border-indigo-200' },
  mixed_negotiation:      { label: 'Mixed Negotiation',      tone: 'bg-purple-50 text-purple-800 border-purple-200' },
  unspecified:            { label: 'Unspecified',            tone: 'bg-gray-50 text-gray-700 border-gray-200' },
};

export default function RevisionExplainerCard({ quotation }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!quotation?.id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data: res } = await axiosInstance.get(
          `/quotation-behavior/explain/${quotation.id}`
        );
        if (!cancelled) setData(res);
      } catch (_) {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [quotation?.id]);

  // R0 / no data = render nothing — keeps the detail panel clean.
  if (loading || !data || data.label === 'unspecified') return null;

  const meta = REASON_META[data.label] || REASON_META.unspecified;
  const s = data.signals || {};

  return (
    <div
      data-testid="quotation-why-changed-card"
      className={`mb-4 rounded-lg border ${meta.tone} p-3 text-sm`}
    >
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-2"
        data-testid="quotation-why-changed-toggle"
      >
        <div className="flex items-center gap-2">
          <Brain className="w-4 h-4" />
          <span className="font-semibold">Why did this change?</span>
          <span className="font-mono text-xs px-2 py-0.5 rounded bg-white/60">
            {meta.label}
          </span>
          <span className="text-xs opacity-70">
            confidence {(data.confidence * 100).toFixed(0)}%
          </span>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {expanded && (
        <div
          data-testid="quotation-why-changed-details"
          className="mt-3 pt-3 border-t border-current/20 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs"
        >
          <Stat label="Patches"       value={s.n_patches} />
          <Stat label="Price edits"   value={s.n_unit_price} pct={s.pct_price} />
          <Stat label="Qty edits"     value={s.n_quantity}    pct={s.pct_quantity} />
          <Stat label="Description edits" value={s.n_description} pct={s.pct_description} />
          {typeof s.avg_price_delta_pct === 'number' && s.n_unit_price > 0 && (
            <Stat
              label="Avg price Δ"
              value={`${(s.avg_price_delta_pct * 100).toFixed(1)}%`}
            />
          )}
          {data.reason_text && (
            <div className="col-span-2 md:col-span-4">
              <div className="opacity-70">Reason note:</div>
              <div className="italic">"{data.reason_text}"</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, pct }) {
  return (
    <div>
      <div className="opacity-70">{label}</div>
      <div className="font-mono font-semibold">
        {value ?? 0}{typeof pct === 'number' ? ` · ${(pct * 100).toFixed(0)}%` : ''}
      </div>
    </div>
  );
}
